import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Lightweight "what is the user looking at" context. The Alice copilot panel
 * reads this to show current-page context and (later) suggested actions. It is
 * deliberately tiny — no AI wiring yet.
 */
export interface ClientPageContext {
  moduleId?: string;
  moduleLabel: string;
  recordId?: string;
  recordLabel?: string;
}

interface PageContextValue {
  context: ClientPageContext;
  setContext: (next: ClientPageContext) => void;
}

const DEFAULT_CONTEXT: ClientPageContext = { moduleLabel: 'Client Portal' };

const PageContextStore = createContext<PageContextValue | null>(null);

export function ClientPageContextProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<ClientPageContext>(DEFAULT_CONTEXT);
  const value = useMemo(() => ({ context, setContext }), [context]);
  return <PageContextStore.Provider value={value}>{children}</PageContextStore.Provider>;
}

export function useClientPageContext(): ClientPageContext {
  const store = useContext(PageContextStore);
  return store?.context ?? DEFAULT_CONTEXT;
}

/**
 * Publishes the current page/record context. Safe to call from any view; the
 * effect only re-runs when the primitive values change.
 */
export function usePublishPageContext(next: ClientPageContext): void {
  const store = useContext(PageContextStore);
  const { moduleId, moduleLabel, recordId, recordLabel } = next;
  useEffect(() => {
    store?.setContext({ moduleId, moduleLabel, recordId, recordLabel });
  }, [store, moduleId, moduleLabel, recordId, recordLabel]);
}
